<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import vClickOutside from '@/utilities/v-click-outside'
import { IconCheckmark, IconChevronDownMedium } from '@cypress-design/vue-icon'

const props = defineProps<{
  modelValue?: string
  error?: string
  label: string
  name: string
  id: string
  options: {
    label: string
    value: string
  }[]
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>()

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      selectedValueRaw.value = value
    }
  },
)

const selectedValueRaw = ref(props.modelValue)
const $opener = ref<HTMLButtonElement>()
const $optionsList = ref<HTMLDivElement>()

const selectedValue = computed(() => {
  return props.options.find((o) => o.value === selectedValueRaw.value)
})

const isActive = ref(false)
const needFlip = ref(false)

const FLIP_MARGIN = 16

watch(isActive, (isActive) => {
  if (isActive) {
    nextTick(() => {
      if ($optionsList.value) {
        const { right } = $optionsList.value.getBoundingClientRect()
        needFlip.value = right + FLIP_MARGIN > window.innerWidth
      } else {
        needFlip.value = false
      }
    })
  } else {
    needFlip.value = false
  }
})

function onInput(value: string) {
  selectedValueRaw.value = value
  $opener.value?.focus()
  emit('update:modelValue', value)
}
</script>

<template>
  <div
    v-click-outside="
      () => {
        isActive = false
      }
    "
    class="relative w-full"
  >
    <input type="hidden" :name="name" :value="selectedValueRaw" />
    <button
      :id="id"
      ref="$opener"
      type="button"
      class="group card flex h-full w-full items-center justify-start rounded bg-transparent py-[8px] pl-[16px] pr-[32px] text-left font-secondary text-[16px] leading-[24px] outline-none"
      :aria-expanded="isActive"
      @click="
        () => {
          isActive = !isActive
        }
      "
    >
      <slot
        v-if="selectedValue"
        name="option"
        v-bind="{
          value: selectedValue.value,
          label: selectedValue.label,
        }"
      >
        <span v-if="selectedValue" class="truncate whitespace-nowrap text-gray-700">{{ selectedValue?.label }}</span>
      </slot>
      <slot
        v-else
        name="option"
        v-bind="{
          label,
        }"
      >
        <span class="pointer-events-none text-gray-400">{{ label }}</span>
      </slot>

      <IconChevronDownMedium
        :class="`absolute right-[12px] transform transition ease-in-out ${
          isActive ? 'rotate-180 text-indigo-500' : 'text-gray-500'
        }`"
      />
    </button>
    <div v-if="isActive" class="absolute bottom-0 left-0 right-0" role="listbox" :aria-label="label">
      <div
        ref="$optionsList"
        class="card absolute top-[8px] z-50 flex w-auto min-w-full flex-col gap-[4px] rounded border bg-white bg-clip-padding p-[8px] text-left text-[14px] shadow-lg"
        :class="needFlip ? 'right-0' : 'left-0'"
      >
        <!-- prettier-ignore -->
        <button
          v-for="{ value, label: optionLabel }, index in options"
          :key="value"
          class="font-secondary text-[16px] leading-[24px] py-[6px] pr-[32px] pl-[8px] group flex items-center rounded hover:bg-indigo-50 text-gray-700 text-left whitespace-nowrap"
          :class="selectedValueRaw === value ? 'bg-indigo-50' : ''"
          role="option"
          type="button"
          @click="
            () => {
              isActive = false
              onInput(value)
            }
          "
        >
          <IconCheckmark
            v-if="selectedValueRaw === value"
            class="right-[16px] absolute"
            stroke-color="indigo-500"
          />
          <slot
            name="option"
            v-bind="{
              value,
              label: optionLabel,
              selected: selectedValueRaw === value,
              index,
            }"
          >
            {{ optionLabel }}
          </slot>
        </button>
      </div>
    </div>
  </div>
</template>
